import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  FileCopy as FileIcon,
} from '@mui/icons-material';
import { Document, DocumentType, User } from '../../types';
import { formatDistanceToNow } from 'date-fns';

interface DocumentListProps {
  documents: Document[];
  loading: boolean;
  currentUser: User;
  typeFilter: DocumentType | 'all';
  approvalFilter: 'all' | 'approved' | 'pending';
  onTypeFilterChange: (filter: DocumentType | 'all') => void;
  onApprovalFilterChange: (filter: 'all' | 'approved' | 'pending') => void;
  onDocumentClick: (document: Document) => void;
  onDeleteDocument: (documentId: string) => void;
  onApproveDocument: (documentId: string) => void;
  onRejectDocument: (documentId: string, reason: string) => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  loading,
  currentUser,
  typeFilter,
  approvalFilter,
  onTypeFilterChange,
  onApprovalFilterChange,
  onDocumentClick,
  onDeleteDocument,
  onApproveDocument,
  onRejectDocument,
}) => {
  // Get icon for document type
  const getDocumentIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();

    if (ext === 'pdf') return <PdfIcon fontSize="large" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'tiff', 'tif'].includes(ext || '')) {
      return <ImageIcon fontSize="large" />;
    }
    return <FileIcon fontSize="large" />;
  };

  // Format document type for display
  const formatDocumentType = (type: DocumentType): string => {
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get approval status color
  const getApprovalStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Check if user can delete document
  const canDelete = (document: Document): boolean => {
    return currentUser.role === 'admin' || document.createdBy === currentUser.uid;
  };

  // Check if user can approve/reject
  const canModerate = currentUser.role === 'admin';

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Document Type</InputLabel>
          <Select
            value={typeFilter}
            label="Document Type"
            onChange={(e) => onTypeFilterChange(e.target.value as DocumentType | 'all')}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="birth-certificate">Birth Certificate</MenuItem>
            <MenuItem value="death-certificate">Death Certificate</MenuItem>
            <MenuItem value="marriage-certificate">Marriage Certificate</MenuItem>
            <MenuItem value="divorce-decree">Divorce Decree</MenuItem>
            <MenuItem value="census-record">Census Record</MenuItem>
            <MenuItem value="military-record">Military Record</MenuItem>
            <MenuItem value="will">Will</MenuItem>
            <MenuItem value="deed">Deed</MenuItem>
            <MenuItem value="letter">Letter</MenuItem>
            <MenuItem value="diary">Diary</MenuItem>
            <MenuItem value="obituary">Obituary</MenuItem>
            <MenuItem value="newspaper-clipping">Newspaper Clipping</MenuItem>
            <MenuItem value="immigration-record">Immigration Record</MenuItem>
            <MenuItem value="naturalization-papers">Naturalization Papers</MenuItem>
            <MenuItem value="school-record">School Record</MenuItem>
            <MenuItem value="employment-record">Employment Record</MenuItem>
            <MenuItem value="medical-record">Medical Record</MenuItem>
            <MenuItem value="tax-record">Tax Record</MenuItem>
            <MenuItem value="bible-record">Bible Record</MenuItem>
            <MenuItem value="church-record">Church Record</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        {canModerate && (
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Approval Status</InputLabel>
            <Select
              value={approvalFilter}
              label="Approval Status"
              onChange={(e) => onApprovalFilterChange(e.target.value as 'all' | 'approved' | 'pending')}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Document Grid */}
      {documents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            No documents found matching the current filters.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {documents.map((document) => (
            <Grid item xs={12} sm={6} md={4} key={document.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
                onClick={() => onDocumentClick(document)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Document Icon */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 80,
                      mb: 2,
                      color: 'primary.main',
                    }}
                  >
                    {getDocumentIcon(document.fileName)}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      minHeight: '3em',
                    }}
                  >
                    {document.title}
                  </Typography>

                  {/* Metadata */}
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      label={formatDocumentType(document.type)}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={document.approvalStatus}
                      size="small"
                      color={getApprovalStatusColor(document.approvalStatus)}
                    />
                    {document.date && (
                      <Typography variant="caption" color="text.secondary">
                        Date: {new Date(document.date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Stack>

                  {/* Description preview */}
                  {document.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {document.description}
                    </Typography>
                  )}

                  {/* Upload info */}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Uploaded {formatDistanceToNow(new Date(document.createdAt))} ago
                  </Typography>
                </CardContent>

                {/* Actions */}
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDocumentClick(document);
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box>
                    {/* Approval actions (Admin only) */}
                    {canModerate && document.approvalStatus === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              onApproveDocument(document.id!);
                            }}
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              const reason = window.prompt('Reason for rejection (optional):');
                              if (reason !== null) {
                                onRejectDocument(document.id!, reason);
                              }
                            }}
                          >
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}

                    {/* Delete action */}
                    {canDelete(document) && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteDocument(document.id!);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default DocumentList;
